import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  CreateUserForExistingPatientSchema,
  LocaleTag,
  PatientMonitoringType,
} from "../../api";
import { Button } from "../../components/ui-kit/button";
import { Radio } from "../../components/ui-kit/fields/radio";
import { Select } from "../../components/ui-kit/fields/select";
import { TextInput } from "../../components/ui-kit/fields/text-input";
import { FormRow } from "../../components/ui-kit/form/form-row";
import { FormTable } from "../../components/ui-kit/form/form-table";
import { deviceHandovers, tokenExpiries } from "../../constants/constants";
import {
  monitoringTypes,
  supportedLanguages,
} from "../../constants/enum-to-text";
import { assert } from "../../utils/utils";
import { Patient } from "./patient";

export const CreateUserForm = observer(function CreateUserForm(props: {
  patient: Patient;
}) {
  const { t } = useLingui();
  const [data] = useState(
    (): {
      token_expiry_timedelta?: string;
      preferred_language?: LocaleTag;
      new_monitoring_type: PatientMonitoringType;
      email: string;
      welcome_email_type?: CreateUserForExistingPatientSchema["welcome_email_type"];
    } =>
      observable({
        token_expiry_timedelta: "P1D",
        preferred_language: LocaleTag.sk,
        new_monitoring_type: PatientMonitoringType.self_measurement,
        email: "",
        welcome_email_type: undefined,
      }),
  );

  return (
    <div>
      <FormTable>
        <FormRow
          label={t`Email (also used as username)`}
          data={<TextInput attr="email" model={data} />}
        />

        <FormRow
          label={t`Device handover (changes welcome email)`}
          data={
            <Radio
              attr="welcome_email_type"
              model={data}
              values={deviceHandovers}
            />
          }
        />

        <FormRow
          label={t`Monitoring type`}
          data={
            <Select
              value={data["new_monitoring_type"]}
              onChange={(newValue) => {
                data["new_monitoring_type"] = newValue;
              }}
              values={monitoringTypes}
            />
          }
        />

        <FormRow
          label={t`Preferred language`}
          data={
            <Select
              attr="preferred_language"
              model={data}
              values={supportedLanguages}
            />
          }
        />

        <FormRow
          label={t`Finish registration link expiration`}
          data={
            <Select
              attr="token_expiry_timedelta"
              model={data}
              values={tokenExpiries}
            />
          }
        />
      </FormTable>

      <br className="h-5" />

      <Button
        variant="primary"
        onClick={async () => {
          assert(data.welcome_email_type, "Welcome email type is required");
          await props.patient.createConnectedUser({
            ...data,
            welcome_email_type: data.welcome_email_type,
          });
        }}
      >
        <Trans>Create user</Trans>
      </Button>
    </div>
  );
});
